import * as React from 'react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API, Auth } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import {
  Search,
  Loader2,
  Shield,
  X,
  Check,
  Ban,
  Users,
  Mail,
  Columns3,
  Eye,
  EyeOff,
} from 'lucide-react';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';
import { getIntegrationXref } from '../../../graphql/queries';

const gql: any = gqlTag;

// ─── Types ──────────────────────────────────────────────────────────────────

interface OrgItem {
  id: string;
  name: string;
  clonedFrom?: string;
  configParms: any;
  featureFlags: Record<string, string[] | string | null>;
  normalizedFlags: Record<string, string[] | null>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

async function fetchFeatureFlagNames(): Promise<string[]> {
  try {
    const response: any = await API.graphql({
      query: getIntegrationXref,
      variables: { id: 'featureFlags' },
    });

    if (response.data?.getIntegrationXref?.mappingJSON) {
      const parsed = JSON.parse(response.data.getIntegrationXref.mappingJSON);
      if (Array.isArray(parsed)) {
        return parsed.sort();
      }
    }

    // Fallback to listIntegrationXrefs
    const listQuery = gql`
      query ListIntegrationXrefs($filter: ModelIntegrationXrefFilterInput) {
        listIntegrationXrefs(filter: $filter) {
          items {
            id
            mappingJSON
          }
        }
      }
    `;
    const listResponse: any = await API.graphql({
      query: listQuery,
      variables: { filter: { id: { eq: 'featureFlags' } } },
    });
    const items = listResponse.data?.listIntegrationXrefs?.items || [];
    if (items.length > 0 && items[0].mappingJSON) {
      const parsed = JSON.parse(items[0].mappingJSON);
      if (Array.isArray(parsed)) {
        return parsed.sort();
      }
    }

    return [];
  } catch (error) {
    console.error('Error fetching feature flags:', error);
    return [];
  }
}

function normalizeFlag(rawValue: any): string[] | null {
  if (Array.isArray(rawValue) && rawValue.length > 0 && rawValue[0] !== '') {
    return rawValue;
  }
  if (rawValue === 'everyone') {
    return ['everyone'];
  }
  if (
    rawValue !== undefined &&
    typeof rawValue === 'string' &&
    rawValue.length > 0
  ) {
    return [rawValue];
  }
  return null;
}

function getFlagDisplay(value: string[] | null): {
  label: string;
  variant: 'disabled' | 'everyone' | 'emails';
  overflow?: number;
} {
  if (!value) {
    return { label: 'Disabled', variant: 'disabled' };
  }
  if (value[0] === 'everyone') {
    return { label: 'Everyone', variant: 'everyone' };
  }
  if (value.length <= 2) {
    return { label: value.join(', '), variant: 'emails' };
  }
  return {
    label: value.slice(0, 2).join(', '),
    variant: 'emails',
    overflow: value.length - 2,
  };
}

// ─── Column Visibility Popover ──────────────────────────────────────────────

function ColumnVisibilityMenu({
  featureFlags,
  visibleFlags,
  onToggle,
  onShowAll,
  onHideAll,
}: {
  featureFlags: string[];
  visibleFlags: Set<string>;
  onToggle: (flag: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className="gap-1.5"
      >
        <Columns3 className="size-3.5" />
        Columns
        <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">
          {visibleFlags.size}/{featureFlags.length}
        </Badge>
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-64 bg-background border border-border rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
            <span className="text-xs font-medium text-foreground">
              Visible Columns
            </span>
            <div className="flex gap-1">
              <button
                onClick={onShowAll}
                className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                All
              </button>
              <button
                onClick={onHideAll}
                className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
              >
                None
              </button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto p-1.5">
            {featureFlags.map((ff) => {
              const isVisible = visibleFlags.has(ff);
              return (
                <button
                  key={ff}
                  onClick={() => onToggle(ff)}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm hover:bg-muted/50 transition-colors"
                >
                  {isVisible ? (
                    <Eye className="size-3.5 text-primary shrink-0" />
                  ) : (
                    <EyeOff className="size-3.5 text-muted-foreground shrink-0" />
                  )}
                  <span
                    className={cn(
                      'truncate',
                      isVisible ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {ff}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Feature Flag Modal ─────────────────────────────────────────────────────

function FeatureFlagModal({
  isOpen,
  onClose,
  featureName,
  selectedOrgs,
  orgsDict,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  featureName: string | null;
  selectedOrgs: string[];
  orgsDict: Record<string, OrgItem>;
  onConfirm: (
    action: 'enable' | 'disable' | 'email',
    orgs: string[],
    emails: string[]
  ) => void;
}) {
  const [emailInput, setEmailInput] = useState('');
  const [emailsList, setEmailsList] = useState<string[]>([]);
  const [confirmAction, setConfirmAction] = useState<
    'enable' | 'disable' | 'email' | null
  >(null);
  const [emailError, setEmailError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  useEffect(() => {
    if (isOpen && selectedOrgs.length === 1 && featureName) {
      const org = orgsDict[selectedOrgs[0]];
      const currentValue = org?.normalizedFlags?.[featureName];
      if (
        currentValue &&
        currentValue[0] !== 'everyone' &&
        currentValue.length > 0
      ) {
        setEmailsList([...currentValue]);
      } else {
        setEmailsList([]);
      }
    } else {
      setEmailsList([]);
    }
    setConfirmAction(null);
    setEmailInput('');
  }, [isOpen, featureName, selectedOrgs, orgsDict]);

  const addEmail = useCallback(() => {
    const trimmed = emailInput.trim();
    if (!trimmed) {
      return;
    }
    if (emailsList.some((e) => e.toLowerCase() === trimmed.toLowerCase())) {
      setEmailInput('');
      setEmailError('');
      return;
    }
    if (emailRegex.test(trimmed)) {
      setEmailsList((prev) => [...prev, trimmed]);
      setEmailInput('');
      setEmailError('');
    } else {
      setEmailError('Invalid email address');
      setTimeout(() => setEmailError(''), 2000);
    }
  }, [emailInput, emailsList]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
        e.preventDefault();
        addEmail();
      }
    },
    [addEmail]
  );

  const removeEmail = useCallback((email: string) => {
    setEmailsList((prev) => prev.filter((e) => e !== email));
  }, []);

  const handleConfirm = useCallback(() => {
    if (!confirmAction) {
      return;
    }
    onConfirm(confirmAction, [...selectedOrgs], emailsList);
    setConfirmAction(null);
    setEmailsList([]);
    setEmailInput('');
  }, [confirmAction, selectedOrgs, emailsList, onConfirm]);

  if (!isOpen || !featureName) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => {
          setConfirmAction(null);
          setEmailsList([]);
          onClose();
        }}
      />
      <div
        className="relative bg-background border border-border rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {featureName}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedOrgs.length} org
                {selectedOrgs.length !== 1 ? 's' : ''} selected
              </p>
            </div>
            <button
              onClick={() => {
                setConfirmAction(null);
                setEmailsList([]);
                onClose();
              }}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {confirmAction ? (
            <div className="space-y-4">
              <p className="text-sm text-foreground font-medium">
                {confirmAction === 'email'
                  ? `Enable ${featureName} for ${emailsList.join(', ')} on ${
                      selectedOrgs[0]
                    }`
                  : `${
                      confirmAction === 'enable' ? 'Enable' : 'Disable'
                    } ${featureName} for all users on ${
                      selectedOrgs.length
                    } org${selectedOrgs.length !== 1 ? 's' : ''}?`}
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleConfirm} className="flex-1">
                  <Check className="size-3.5 mr-1.5" />
                  Confirm
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmAction(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmAction('disable')}
                  className="flex-1"
                >
                  <Ban className="size-3.5 mr-1.5" />
                  Disable All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmAction('enable')}
                  className="flex-1"
                >
                  <Users className="size-3.5 mr-1.5" />
                  Enable Everyone
                </Button>
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium text-foreground mb-1">
                  Enable for specific users
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Enter emails separated by space or enter. Click an email to
                  remove it.
                </p>

                {emailsList.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {emailsList.map((email) => (
                      <button
                        key={email}
                        onClick={() => removeEmail(email)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20 transition-colors"
                      >
                        {email}
                        <X className="size-3" />
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      selectedOrgs.length > 1
                        ? 'Available for single org only'
                        : 'Enter user email'
                    }
                    disabled={selectedOrgs.length > 1}
                    className="flex-1 h-9 text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addEmail}
                    disabled={selectedOrgs.length > 1 || !emailInput.trim()}
                  >
                    Add
                  </Button>
                </div>

                {emailError && (
                  <p className="text-xs text-destructive mt-1.5">
                    {emailError}
                  </p>
                )}

                {emailsList.length > 0 && selectedOrgs.length === 1 && (
                  <Button
                    size="sm"
                    className="mt-3 w-full"
                    onClick={() => setConfirmAction('email')}
                  >
                    <Mail className="size-3.5 mr-1.5" />
                    Save Email Access
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function FeatureFlagsPage() {
  const [orgs, setOrgs] = useState<OrgItem[]>([]);
  const [orgsDict, setOrgsDict] = useState<Record<string, OrgItem>>({});
  const [featureFlags, setFeatureFlags] = useState<string[]>([]);
  const [visibleFlags, setVisibleFlags] = useState<Set<string>>(new Set());
  const [checkedOrgs, setCheckedOrgs] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  const [hideSeedOrgs, setHideSeedOrgs] = useState(true);
  const [loading, setLoading] = useState(true);
  const [currentFeature, setCurrentFeature] = useState<string | null>(null);
  const toast = useToast();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const cognitoUser = await Auth.currentAuthenticatedUser();
      const groups =
        cognitoUser.signInUserSession?.accessToken?.payload?.[
          'cognito:groups'
        ] || [];
      if (!groups.includes('paiv_admin_rw')) {
        toast.error('Access Denied', 'You do not have admin access.');
        return;
      }

      const [flagNames, orgItems] = await Promise.all([
        fetchFeatureFlagNames(),
        fetchAllOrgs(),
      ]);

      setFeatureFlags(flagNames);
      setVisibleFlags(new Set(flagNames));

      const checkedTemp: Record<string, boolean> = {};
      const dictTemp: Record<string, OrgItem> = {};

      const parsedOrgs: OrgItem[] = orgItems.map((raw: any) => {
        let configParms: any = {};
        try {
          configParms = JSON.parse(raw.configParms || '{}');
        } catch {
          // ignore
        }

        const featureFlagsRaw = configParms.featureFlags || {};
        const normalizedFlags: Record<string, string[] | null> = {};

        flagNames.forEach((ff) => {
          normalizedFlags[ff] = normalizeFlag(featureFlagsRaw[ff]);
        });

        const org: OrgItem = {
          id: raw.id,
          name: raw.name || raw.id,
          clonedFrom: raw.clonedFrom || undefined,
          configParms,
          featureFlags: featureFlagsRaw,
          normalizedFlags,
        };

        checkedTemp[raw.id] = false;
        dictTemp[raw.id] = org;
        return org;
      });

      parsedOrgs.sort((a, b) => (a.id || '').localeCompare(b.id || ''));
      setOrgs(parsedOrgs);
      setOrgsDict(dictTemp);
      setCheckedOrgs(checkedTemp);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Error', 'Failed to load feature flags data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllOrgs = async () => {
    let allItems: any[] = [];
    let nextToken: string | null = null;

    do {
      const myQuery = gql`
        query MyQuery($limit: Int, $nextToken: String) {
          listOrgs(limit: $limit, nextToken: $nextToken) {
            items {
              id
              configParms
              name
              clonedFrom
            }
            nextToken
          }
        }
      `;
      const res: any = await API.graphql({
        query: myQuery,
        variables: { limit: 100, nextToken },
      });
      const { items, nextToken: newToken } = res.data.listOrgs;
      allItems = allItems.concat(items);
      nextToken = newToken;
    } while (nextToken);

    return allItems;
  };

  const filteredOrgs = useMemo(() => {
    let result = orgs;

    if (hideSeedOrgs) {
      result = result.filter(
        (org) => org.clonedFrom !== 'paivcam-seed' && org.id !== 'paivcam-seed'
      );
    }

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (org) =>
          org.id.toLowerCase().includes(term) ||
          org.name.toLowerCase().includes(term)
      );
    }

    return result;
  }, [orgs, search, hideSeedOrgs]);

  const displayedFlags = useMemo(
    () => featureFlags.filter((ff) => visibleFlags.has(ff)),
    [featureFlags, visibleFlags]
  );

  // Sync top scrollbar with table scroll
  useEffect(() => {
    const table = tableContainerRef.current;
    const topScroll = topScrollRef.current;
    if (!table || !topScroll) {
      return;
    }

    const onTableScroll = () => {
      if (syncing.current) {
        return;
      }
      syncing.current = true;
      topScroll.scrollLeft = table.scrollLeft;
      syncing.current = false;
    };

    const onTopScroll = () => {
      if (syncing.current) {
        return;
      }
      syncing.current = true;
      table.scrollLeft = topScroll.scrollLeft;
      syncing.current = false;
    };

    table.addEventListener('scroll', onTableScroll);
    topScroll.addEventListener('scroll', onTopScroll);

    return () => {
      table.removeEventListener('scroll', onTableScroll);
      topScroll.removeEventListener('scroll', onTopScroll);
    };
  }, [loading, visibleFlags]);

  // Track table scroll width for the top scrollbar
  useEffect(() => {
    const table = tableContainerRef.current;
    if (!table) {
      return;
    }
    const updateWidth = () => setTableScrollWidth(table.scrollWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(table);
    return () => observer.disconnect();
  }, [loading, visibleFlags]);

  const selectedOrgIds = useMemo(
    () => Object.keys(checkedOrgs).filter((id) => checkedOrgs[id]),
    [checkedOrgs]
  );

  const toggleOrg = useCallback((orgId: string) => {
    setCheckedOrgs((prev) => ({ ...prev, [orgId]: !prev[orgId] }));
  }, []);

  const deselectAll = useCallback(() => {
    setCheckedOrgs((prev) => {
      const next: Record<string, boolean> = {};
      for (const key in prev) {
        next[key] = false;
      }
      return next;
    });
  }, []);

  const handleCellClick = useCallback((orgId: string, featureName: string) => {
    setCheckedOrgs((prev) => ({ ...prev, [orgId]: true }));
    setCurrentFeature(featureName);
  }, []);

  const handleHeaderClick = useCallback(
    (featureName: string) => {
      if (selectedOrgIds.length >= 1) {
        setCurrentFeature(featureName);
      }
    },
    [selectedOrgIds]
  );

  const toggleFlagVisibility = useCallback((flag: string) => {
    setVisibleFlags((prev) => {
      const next = new Set(prev);
      if (next.has(flag)) {
        next.delete(flag);
      } else {
        next.add(flag);
      }
      return next;
    });
  }, []);

  const showAllFlags = useCallback(() => {
    setVisibleFlags(new Set(featureFlags));
  }, [featureFlags]);

  const hideAllFlags = useCallback(() => {
    setVisibleFlags(new Set());
  }, []);

  const handleUpdate = useCallback(
    async (
      action: 'enable' | 'disable' | 'email',
      orgIds: string[],
      emails: string[]
    ) => {
      const updateMutation = gql`
        mutation UpdateOrg($input: UpdateOrgInput!) {
          updateOrg(input: $input) {
            id
            configParms
          }
        }
      `;

      let successCount = 0;

      for (const orgId of orgIds) {
        const org = orgsDict[orgId];
        if (!org) {
          continue;
        }

        const newConfigParms = { ...org.configParms };
        if (!newConfigParms.featureFlags) {
          newConfigParms.featureFlags = {};
        }

        if (action === 'enable') {
          newConfigParms.featureFlags[currentFeature!] = ['everyone'];
        } else if (action === 'disable') {
          delete newConfigParms.featureFlags[currentFeature!];
        } else if (action === 'email') {
          newConfigParms.featureFlags[currentFeature!] = emails;
        }

        try {
          await API.graphql({
            query: updateMutation,
            variables: {
              input: {
                id: orgId,
                configParms: JSON.stringify(newConfigParms),
              },
            },
          });

          setOrgsDict((prev) => {
            const updated = { ...prev };
            const updatedOrg = { ...updated[orgId] };
            updatedOrg.configParms = newConfigParms;
            updatedOrg.featureFlags = newConfigParms.featureFlags;

            if (!updatedOrg.normalizedFlags) {
              updatedOrg.normalizedFlags = {};
            }

            if (action === 'enable') {
              updatedOrg.normalizedFlags[currentFeature!] = ['everyone'];
            } else if (action === 'disable') {
              updatedOrg.normalizedFlags[currentFeature!] = null;
            } else if (action === 'email') {
              updatedOrg.normalizedFlags[currentFeature!] = emails;
            }

            updated[orgId] = updatedOrg;
            return updated;
          });

          successCount++;
        } catch (err) {
          console.error('Error updating org:', orgId, err);
        }
      }

      if (successCount > 0) {
        toast.success(
          'Updated',
          `${currentFeature} updated for ${successCount} org${
            successCount !== 1 ? 's' : ''
          }.`
        );
      }

      setCurrentFeature(null);
    },
    [orgsDict, currentFeature, toast]
  );

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Feature Flags"
          actions={[
            {
              id: 'admin-badge',
              label: 'Admin Only',
              icon: <Shield className="size-4" />,
              variant: 'secondary' as const,
            },
          ]}
        />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Feature Flags"
        actions={[
          {
            id: 'admin-badge',
            label: 'Admin Only',
            icon: <Shield className="size-4" />,
            variant: 'secondary' as const,
          },
        ]}
      />

      <div className="flex-1 overflow-hidden flex flex-col p-4 sm:p-6 gap-3">
        {/* Instructions + selection bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <p className="text-sm text-foreground">
              Click a cell to update, or select orgs and click a column header
              for bulk updates.
            </p>
            <p className="text-xs text-muted-foreground">
              * Email access can only be set for one org at a time
            </p>
          </div>
          {selectedOrgIds.length > 0 && (
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="secondary">
                {selectedOrgIds.length} selected
              </Badge>
              <button
                onClick={deselectAll}
                className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Deselect All
              </button>
            </div>
          )}
        </div>

        {/* Controls row */}
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search by org ID or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideSeedOrgs}
              onChange={(e) => setHideSeedOrgs(e.target.checked)}
              className="accent-primary size-3.5"
            />
            Hide seed orgs
          </label>
          <ColumnVisibilityMenu
            featureFlags={featureFlags}
            visibleFlags={visibleFlags}
            onToggle={toggleFlagVisibility}
            onShowAll={showAllFlags}
            onHideAll={hideAllFlags}
          />
        </div>

        {/* Top scrollbar - mirrors the table horizontal scroll */}
        <div
          ref={topScrollRef}
          className="overflow-x-auto overflow-y-hidden border border-border rounded-t-lg shrink-0"
          style={{ height: '12px' }}
        >
          <div
            style={{
              width: tableScrollWidth > 0 ? tableScrollWidth : '100%',
              height: '1px',
            }}
          />
        </div>

        {/* Table container with contained scroll */}
        <div
          ref={tableContainerRef}
          className="flex-1 overflow-auto border border-border rounded-b-lg -mt-px"
        >
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-muted border-b border-border">
                {/* Frozen org column */}
                <th className="sticky left-0 z-20 bg-muted text-left px-3 py-2.5 font-semibold text-primary min-w-[200px] border-r border-border shadow-[3px_0_6px_-3px_rgba(0,0,0,0.1)]">
                  Org
                </th>
                {displayedFlags.map((ff) => (
                  <th
                    key={ff}
                    onClick={() => handleHeaderClick(ff)}
                    className={cn(
                      'text-left px-3 py-2.5 font-semibold text-primary whitespace-nowrap min-w-[120px]',
                      selectedOrgIds.length >= 1 &&
                        'cursor-pointer hover:bg-primary/5 transition-colors'
                    )}
                  >
                    {ff.charAt(0).toUpperCase() + ff.slice(1)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredOrgs.map((org) => {
                const isChecked = checkedOrgs[org.id] || false;
                const flags = orgsDict[org.id]?.normalizedFlags || {};

                return (
                  <tr
                    key={org.id}
                    className={cn(
                      'transition-colors',
                      isChecked ? 'bg-primary/5' : 'hover:bg-muted/30'
                    )}
                  >
                    {/* Frozen org cell */}
                    <td
                      className={cn(
                        'sticky left-0 z-10 px-3 py-2 min-w-[200px] border-r border-border shadow-[3px_0_6px_-3px_rgba(0,0,0,0.1)]',
                        isChecked
                          ? 'bg-blue-50 dark:bg-blue-950/40'
                          : 'bg-white dark:bg-[hsl(var(--background))]'
                      )}
                    >
                      <label className="flex items-center gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleOrg(org.id)}
                          className="size-4 rounded border-border text-primary focus:ring-primary accent-primary shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-mono text-foreground truncate leading-tight">
                            {org.id}
                          </p>
                          {org.name && org.name !== org.id && (
                            <p className="text-[11px] text-muted-foreground truncate leading-tight">
                              {org.name}
                            </p>
                          )}
                        </div>
                      </label>
                    </td>
                    {displayedFlags.map((ff) => {
                      const display = getFlagDisplay(flags[ff]);
                      return (
                        <td
                          key={ff}
                          onClick={() => handleCellClick(org.id, ff)}
                          className="px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors max-w-[200px]"
                        >
                          <span
                            className={cn(
                              'text-xs block truncate',
                              display.variant === 'disabled' &&
                                'text-muted-foreground',
                              display.variant === 'everyone' &&
                                'text-emerald-600 dark:text-emerald-400 font-medium',
                              display.variant === 'emails' &&
                                'text-blue-600 dark:text-blue-400'
                            )}
                          >
                            {display.label}
                            {display.overflow ? (
                              <span className="text-muted-foreground ml-1">
                                +{display.overflow}
                              </span>
                            ) : null}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredOrgs.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No organizations found.
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-xs text-muted-foreground shrink-0">
          {filteredOrgs.length} organization
          {filteredOrgs.length !== 1 ? 's' : ''} &middot;{' '}
          {displayedFlags.length} of {featureFlags.length} columns visible
        </p>
      </div>

      {/* Modal */}
      <FeatureFlagModal
        isOpen={Boolean(currentFeature)}
        onClose={() => setCurrentFeature(null)}
        featureName={currentFeature}
        selectedOrgs={selectedOrgIds}
        orgsDict={orgsDict}
        onConfirm={handleUpdate}
      />
    </div>
  );
}
